/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { STEPS, TENANTS } from "../constants"
import { FONT_TYPES } from "../constants/Fonts"
import { AccessKeysContext } from "../context/AccessKeysContext"
import EvollisPagesLayout from "../digital-journey/EvollisPagesLayout"
import { PackServiceProvider } from "../digital-journey/ServicePage/context/PackServiceContext"
import { TunnelDataContext } from "../digital-journey/context/TunnelDataContext"
import HomePage from "../pages/home"
import { LoginPage } from "../pages/login"
import ResetPasswordPage from "../pages/reset-password/ResetPasswordPage"
import ResumeOrder from "../pages/resumeOrder"
import SimulationPage from "../pages/simulation"
import { Logger } from "../service/Logger"
import { isUserConnected, resetTokenCookie } from "../service/authorization"
import ApiService from "./service/ApiService"
import {
  LOCAL_STORAGE_TUNNEL_DATA_KEY,
  didUserRefreshedPage,
  flagTunnelDataForPaylineNavigationCases,
  getClientIPAddress,
  sendToLogServer,
  updateLocalStorageLastPathNameVisited
} from "./service/BusinessLogicService"
import {
  authenticationService,
  getCookiesAccessToken,
  isPdiRetailRoutes,
  isPdiRetailTenant,
  mapBackEndDateToMomentObject,
  resolvePdiRetailBaseUrl,
  setVoyageurLanguage,
  userConfigurationService
} from "./utils"
import { setThemeColorFromTunnelData } from "./utils.theme.js"
import { getSessionVoyageurLang, normalizeVoyageurLangCode } from "./voyageurLanguage"
import AdyenCheckout from "@adyen/adyen-web"
import DOMPurify from "dompurify"
import moment from "moment"
import React, { Component } from "react"
import { Cookies } from "react-cookie"
import { Route, Switch } from "react-router-dom"
import { getOrderStorageDays } from "./constants/orderStorage"

const errorCodeDetectionRegex = /[0-9]{3}/g
const netheosRestartUrlDetectionRegex = /\/uuid\/(.+)\/(.*)/
const HISTORY_POP_ACTION = "POP"
const HISTORY_PUSH_ACTION = "PUSH"

class PagesLayout extends Component {
  state = {
    tunnelData: null,
    /** Incrémenté à chaque `voyageurLanguageChange` pour forcer le re-render du parcours et ré-exécuter `loc()`. */
    voyageurLangRevision: 0
  }

  currentPathname = null

  _onVoyageurLanguageChange = () => {
    this.setState((s) => ({ voyageurLangRevision: s.voyageurLangRevision + 1 }))
  }

  async componentDidMount() {
    window.addEventListener("voyageurLanguageChange", this._onVoyageurLanguageChange)
    const { history, incrementLoading, decrementLoading } = this.props
    const query = new URLSearchParams(this.props.location.search)
    let selectedServicePack = query.get("pack")
    let selectedSku = query.get("sku")
    let tenant = query.get("tenant")
    let opl = query.get("opl")
    let deliveryMethod = query.get("deliveryMethod")
    let forceBackToSimulation = query.get("forceBackSimulation")
    const pdiLogout = query.get("pdiLogout")
    let adyenSessionId = query.get("sessionId")
    let redirectResult = query.get("redirectResult")
    const isJourneyResumedAtHome = query.get("isJourneyResumedAtHome") === "true"

    let lang = query.get("lang") ? query.get("lang") : "fr-FR"
    let paylineToken = query.get("token")
    if (!paylineToken) {
      paylineToken = query.get("paylinetoken")
    }
    let transactionId = query.get("transactionId")

    if (pdiLogout) {
      sessionStorage.clear()
      localStorage.clear()
      resetTokenCookie()
    }

    this.backListener = history.listen((newLocation, action) => {
      const { pathname } = newLocation

      if (!isUserConnected()) {
        if (action === HISTORY_PUSH_ACTION) {
          if (pathname !== this.currentPathname) {
            this.currentPathname = pathname

            history.push(pathname)
          }
        } else {
          history.go(1)
        }
      }

      if (isUserConnected()) {
        if (action === HISTORY_PUSH_ACTION) {
          if (pathname === "/login") {
            this.currentPathname = pathname

            history.push("/home")
          }
        }

        if (action === HISTORY_POP_ACTION) {
          if (isPdiRetailRoutes(pathname)) {
            history.push("/home")
          } else {
            // must do this shit because we need to re-launch the web app and retrieve the tunnelData shit and to not keep the digital-journey opl tunnelData.
            // if we do a Browser back button, we keep in localStorage the previous tunnelData and step...
            window.location.href = `${tunnelData.pdiRetailUrl}login?tenant=${tunnelData.tenant}`
          }
        }
      }
    })

    let tunnelData = {}
    tunnelData.lang = lang
    tunnelData.opl = opl
    tunnelData.tenant = tenant || JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_TUNNEL_DATA_KEY))?.tenant
    tunnelData.adyenSessionId = adyenSessionId
    tunnelData.redirectResult = redirectResult
    tunnelData.isJourneyResumedAtHome = isJourneyResumedAtHome
    const pathName = window.location.pathname.toString()
    const isPaylineReturn = pathName.includes("payline")

    console.log(`%c::::: COMING FROM URL :::::\n`, "color:red; font-size: 14px", `${window.location.href}`)

    ApiService.register({ history, incrementLoading, decrementLoading })

    const cookies = new Cookies()
    const hasEvollisAuthCookies =
      !!cookies.get("TOKEN") ||
      (!!cookies.get("TOKEN_PART_ONE") &&
        !!cookies.get("TOKEN_PART_TWO") &&
        !!cookies.get("TOKEN_HEADER") &&
        !!cookies.get("TOKEN_OPTIONS"))

    if (
      (tunnelData.tenant === TENANTS.RETAIL_IT ||
        tunnelData.tenant === TENANTS.RETAIL_FNAC_PT ||
        tunnelData.tenant === TENANTS.MEDIAMARKT_NL ||
        tunnelData.tenant === TENANTS.MEDIAMARKT_BE ||
        tunnelData.tenant === TENANTS.MORTAR ||
        tunnelData.tenant === TENANTS.BUT ||
        tunnelData.tenant === TENANTS.CONFORAMA_RETAIL) &&
      hasEvollisAuthCookies
    ) {
      const { cookieToken, cookieHeader, cookieOptions } = getCookiesAccessToken()

      const formattedSessionToken = `${cookieHeader}.${cookieToken}.${cookieOptions}`
      sessionStorage.setItem("user_token", formattedSessionToken)
    }
    // PDI Journey will always be initialized by an url param (case netheos restart, click and collect START or classic START)
    // In all those case, we clean local storage to allow user to restart from shop if needed
    if (
      pathName.includes("/uuid") ||
      (selectedSku && selectedServicePack && tenant) ||
      (opl && !pathName.includes("adyenCheckout") && !isPaylineReturn)
    ) {
      if (!isPdiRetailTenant(tunnelData.tenant)) window.localStorage.clear()
      tunnelData.clientIpAddress = await getClientIPAddress(tunnelData)
    }
    tunnelData.clientIpAddressHeader = await getClientIPAddress(tunnelData)

    tunnelData = this.handleNetheosRestartURLForNavigationCases(tunnelData)
    tunnelData = await this.handleUrlParamFromPaylineAndFlagTunnelDataForNavigationCases(
      tunnelData,
      transactionId,
      opl,
      paylineToken,
      tenant
    )

    authenticationService.userHasLoggedIn() // previous call came back with a valid token => we are now logged in
    tunnelData.authenticationToken = sessionStorage.getItem("user_token")
    this.handleClickAndCollect(selectedSku, selectedServicePack, tenant, tunnelData, deliveryMethod) // Case Click and Collect simplified journey handling
    const constStep = tunnelData.step
    tunnelData = await this.handleInitJourney(tunnelData, query)
    if (constStep) {
      tunnelData.step = constStep
    }

    const resolvedPdiRetailUrl = resolvePdiRetailBaseUrl(tunnelData, query)
    if (resolvedPdiRetailUrl && isPdiRetailTenant(tunnelData.tenant)) {
      tunnelData.pdiRetailUrl = resolvedPdiRetailUrl
      window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(tunnelData))
    }

    let isTransactionActive = true
    const orderStorageTime = getOrderStorageDays(tunnelData?.tenant)

    // We only want the following code executed when we are on journey pages
    // When we first use the retail part, this element is not initialized,
    // But when we go back to simulation from Identification page, we will have it
    if (tunnelData?.data?.order) {
      // @Todo : make sure that we do not execute this in "Back to simulation" case
      const { creationDate } = tunnelData.data.order

      if (creationDate) {
        const parsedDate = mapBackEndDateToMomentObject(creationDate)
        if (orderStorageTime) {
          const simulationLimitDate = parsedDate.add(orderStorageTime, "days")
          isTransactionActive = simulationLimitDate.isAfter(moment())
        }
      }
      if (orderStorageTime && tunnelData?.data?.order && !isTransactionActive) {
        const title = "Simulation expirée"
        const message =
          "La simulation de votre location longue durée que vous avez faite en magasin a expiré. N'hésitez pas à vous rendre en magasin pour réaliser une nouvelle demande de location."
        this.props.history.push("/digital-journey/fail", {
          title: title,
          message: message
        })
      }
    }

    let user = this.setApplicationLangageFromCustomerIfNeeded(tunnelData)
    await new Promise((resolve) => this.setState({ tunnelData, user }, resolve))
    const isSuccessfulPaylineReturn =
      isPaylineReturn && tunnelData.paylineError === false && tunnelData.eliminatoryPaylineError === false

    if (this.props.location.pathname.includes("adyenCheckout")) {
      await this.handleAdyenReturn(tunnelData)
    } else {
      if (forceBackToSimulation === "true" && isTransactionActive) {
        tunnelData.step = "stepHome"
        window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(tunnelData))
        this.props.history.push("/simulation/" + opl)
      } else if (isSuccessfulPaylineReturn && isTransactionActive) {
        tunnelData.step = STEPS.CB
        window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(tunnelData))
        await new Promise((resolve) => this.setState({ tunnelData }, resolve))
        await this.handleSetStep()
      } else if (isTransactionActive) {
        const newStep = this.findCurrentStepFromTunnelData(tunnelData)
        this.props.history.push(newStep.route)
        updateLocalStorageLastPathNameVisited()
      }
    }
    document.title = tunnelData.title || ""
    document.getElementById("icon").href =
      process.env.PUBLIC_URL + "/images/" + tunnelData.pathIcon || process.env.PUBLIC_URL + "/faicon.ico"
    document.getElementById("payline-style").href = tunnelData?.config?.paylineWidgetStyle
    Logger.info("Affichage de la page " + tunnelData.step, false, tunnelData)
  }

  handleClickAndCollect(selectedSku, selectedServicePack, tenant, tunnelData, deliveryMethod) {
    if (!(selectedSku && selectedServicePack && tenant)) {
      return
    }
    tunnelData.clickAndCollectJourney = true
    tunnelData.deliveryMethod = deliveryMethod
    tunnelData.selectedSku = selectedSku
    tunnelData.selectedServicePack = selectedServicePack
  }

  async handleAdyenReturn(tunnelData) {
    const adyenConfig = tunnelData.adyen
    const configuration = {
      environment: adyenConfig.environment, // Change to 'live' for the live environment.
      clientKey: adyenConfig.clientKey, // Unique identifier for the payment session.
      session: {
        id: tunnelData.adyenSessionId
      },
      onPaymentCompleted: (result, component) => {
        let { tunnelData } = this.state
        this.setState({
          tunnelData: {
            ...tunnelData,
            step: "stepCB",
            adyenError: result.resultCode !== "Authorised",
            ayduenResult: result
          }
        })
        sendToLogServer("INFO", "Adyen result for " + tunnelData.opl + " : " + JSON.stringify(result))
        this.handleSetStep(tunnelData)
      },
      onError: (error, component) => {
        let { tunnelData } = this.state
        let adyenError = true
        if ((tunnelData.tenant === TENANTS.MEDIAMARKT_NL || tunnelData.tenant === TENANTS.MEDIAMARKT_BE || tunnelData.tenant === TENANTS.SAMSUNG_NL) && JSON.stringify(error).includes("NETWORK_ERROR"))
          adyenError = false //Si on a le network error et que le tenant est mediamarkt ou samsung nl, alors on ignore cette erreur car on l'étape de vérification
        this.setState({
          tunnelData: {
            ...tunnelData,
            step: "stepCB",
            adyenError
          }
        })
        sendToLogServer("ERROR", "Adyen result for " + tunnelData.opl + " : " + JSON.stringify(error))
        this.handleSetStep(tunnelData)
      }
    }
    // Create an instance of AdyenCheckout using the configuration object.
    const checkout = await AdyenCheckout(configuration)

    await checkout.submitDetails({
      details: { redirectResult: tunnelData.redirectResult }
    })
  }

  async handleInitJourney(tunnelData, query) {
    let hasUserRefreshPage = didUserRefreshedPage()

    if (hasUserRefreshPage) {
      const tunnelDataLocalStorage = window.localStorage.getItem(LOCAL_STORAGE_TUNNEL_DATA_KEY)
      if (!!tunnelDataLocalStorage) return JSON.parse(tunnelDataLocalStorage)
    }

    const scriptUrl = "/api/script/runs/" + tunnelData.tenant + "/first-init"

    let result = await ApiService.post(scriptUrl, { tunnelData })

    if (!result?.tunnelData) return this.navigateToCustomFailPage(result, tunnelData)

    const tunnelDataStringified = JSON.stringify(result?.tunnelData)
    window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, tunnelDataStringified)
    return result.tunnelData
  }

  navigateToCustomFailPage(requestResult, tunnelData) {
    this.setApplicationLangageFromCustomerIfNeeded(tunnelData)
    let errorCode = parseInt(requestResult.toString().match(errorCodeDetectionRegex), 10)
    let title = tunnelData?.failPageMessages?.[errorCode]?.title
    let message = tunnelData?.failPageMessages?.[errorCode]?.message
    this.props.history.push("/digital-journey/fail", {
      title: title,
      message: message
    })
  }

  /**
   * Aligne `voyageur_lang` (et donc le sélecteur) sur `order.orderLanguageTag` (DB),
   * sans écraser une session déjà cohérente.
   */
  syncVoyageurSessionFromOrderLanguageTag(tunnelData) {
    const orderTag = tunnelData?.data?.order?.orderLanguageTag
    if (!orderTag) return
    const langCode = normalizeVoyageurLangCode(orderTag)
    if (!langCode || langCode === getSessionVoyageurLang()) return
    setVoyageurLanguage(langCode, tunnelData?.supportedLanguages)
  }

  setApplicationLangageFromCustomerIfNeeded(tunnelData) {
    let user
    if (userConfigurationService.getUserConfiguration() === null) {
      // set locale for user (both shop and server can define it just before)
      user = tunnelData && {
        locale: tunnelData?.data?.order?.orderLanguageTag
          ? tunnelData.data?.order?.orderLanguageTag
          : tunnelData?.lang || "fr-FR"
      }
      // this is neededby the "loc" API
      userConfigurationService.register({ user })
    } else {
      user = userConfigurationService.getUserConfiguration()
    }
    this.syncVoyageurSessionFromOrderLanguageTag(tunnelData)
    return user
  }

  handleNetheosRestartURLForNavigationCases(tunnelData) {
    tunnelData.netheosRestart = false
    let pathName = window.location.pathname.toString()
    let financer = new URLSearchParams(window.location.search).get("financer")
    if (pathName.includes("/uuid")) {
      console.log("netheos restart detected in PagesLayout")
      let tenantDetection = pathName.match(netheosRestartUrlDetectionRegex)
      tunnelData.netheosRestart = true
      tunnelData.tenant = tenantDetection[1]
      tunnelData.urlUUIDToken = tenantDetection[2]
      tunnelData.financer = financer
      tunnelData.step = "stepNetheos"
    }
    return tunnelData
  }

  async handleUrlParamFromPaylineAndFlagTunnelDataForNavigationCases(tunnelData, transactionId, opl, token, tenant) {
    if (window.location.pathname.includes("payline")) {
      tunnelData = await flagTunnelDataForPaylineNavigationCases(tunnelData, transactionId, token)
      tunnelData.tenant = tenant
      tunnelData.opl = opl
    }
    return tunnelData
  }

  removeTunnelDataError = (path) => {
    const { tunnelData } = this.state
    this.setState({
      tunnelData: {
        ...tunnelData,
        errors: tunnelData.errors.filter((error) => error.path !== path)
      }
    })
  }

  handleSetStep = async (data = {}, isApiCallWithLoader = true) => {
    const { history } = this.props
    const { tunnelData } = this.state
    try {
      let newTunnelData = {
        ...tunnelData,
        data: { ...tunnelData.data, ...data }
      }

      const currentStep = this.findCurrentStepFromTunnelData(tunnelData)
      console.log(
        `%cPagesLayout-HandleSetStep :\nTUNNELDATA BEFORE calling Script server :\n`,
        "color:blue; font-size: 14px",
        "tunneldata : ",
        tunnelData,
        `\n\nCURRENT STEP : \nid : ${currentStep.id}, route: ${currentStep.route}\nRUN script ${currentStep.runUrl}`
      )

      const result = isApiCallWithLoader
        ? await ApiService.post(currentStep.runUrl, {
            tunnelData: newTunnelData
          })
        : await ApiService.postForEvollisApi(currentStep.runUrl, {
            tunnelData: newTunnelData
          })

      let nextTunnelData
      if (result && result.tunnelData) {
        console.log(
          `%cPagesLayout-HandleSetStep :\nReceiving TUNNELDATA AFTER calling Script server :\n`,
          "color:darkgreen; font-size: 18px",
          "new tunneldata : ",
          result.tunnelData,
          `\n\nNEW STEP : \nid : ${result.tunnelData.step}`
        )

        nextTunnelData = result.tunnelData
        window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(nextTunnelData))
        const newStep = this.findCurrentStepFromTunnelData(nextTunnelData)
        this.setState({ tunnelData: nextTunnelData })
        this.setApplicationLangageFromCustomerIfNeeded(nextTunnelData)
        if (newStep.id === STEPS.ALGOAN) {
          nextTunnelData.step = STEPS.ALGOAN_BACK
          window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(nextTunnelData))
          window.location.replace(newStep.route)
          return
        }
        if (
          isPdiRetailTenant(tunnelData.tenant) &&
          window.location.pathname === "/home" &&
          this.props.history.action === "POP"
        ) {
          tunnelData.step = "stepHome"
          window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(tunnelData))
          history.push("/home")
          updateLocalStorageLastPathNameVisited()
        } else {
          history.push(newStep.route)
          updateLocalStorageLastPathNameVisited()
        }
      } else {
        this.navigateToCustomFailPage(result, tunnelData)
      }
    } catch (error) {
      console.error("PagesLayout handleSetStep error : ", error)
    }
  }

  findCurrentStepFromTunnelData(tunnelData) {
    const stepId = tunnelData?.step
    const stepIndex = tunnelData?.breadcrumbs?.findIndex((step) => step.id === stepId)
    const currentStep = tunnelData?.breadcrumbs[stepIndex]
    if (currentStep) {
      return currentStep
    } else {
      throw new Error(`Error : no step with id ${stepId} has been found in tunnelData`)
    }
  }

  componentWillUnmount() {
    window.removeEventListener("voyageurLanguageChange", this._onVoyageurLanguageChange)
    this.backListener()
  }

  render() {
    const { tunnelData } = this.state
    setThemeColorFromTunnelData(tunnelData)

    let componentProps = {
      ...this.props,
      ...this.state,
      onSetStep: this.handleSetStep,
      tunnelData: tunnelData,
      removeTunnelDataError: this.removeTunnelDataError
    }
    let i = 1
    if (!tunnelData) {
      return <div></div>
    } else {
      return (
        <AccessKeysContext.Provider value={tunnelData ? tunnelData.accessKeys : {}}>
          <style
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                [
                  "a {",
                  tunnelData?.links,
                  "}",
                  "a:hover {",
                  tunnelData?.linksHover,
                  "}",
                  ".title-step {",
                  tunnelData?.titleStep,
                  "}",
                  ".inline-steps > li.is-active, .list-steps > li.is-active {",
                  tunnelData?.breadcrumbIsActive,
                  "}",
                  ".btn-primary {",
                  tunnelData?.buttonPrimary,
                  "}",
                  "h1 .title-name {",
                  tunnelData?.titleName,
                  "}",
                  "h1, h2, h3, h4 {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".h1 {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  "h1 .title-step {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".header-info .counter {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".react-select__control {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".form-control {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".react-select__single-value .css-1uccc91-singleValue",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  "body {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  "body, .inline-steps > li {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  "footer p > small {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".btn {",
                  tunnelData?.buttonsUppercased,
                  "}",
                  ".btn-success {",
                  tunnelData?.buttonsUppercased,
                  "}",
                  ".footer-fixed {",
                  tunnelData?.buttonsUppercased,
                  "}",
                  ".included-services p {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".basket .text-right {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  "label {",
                  tunnelData?.customFonts?.[FONT_TYPES.BODY] || tunnelData?.conforamaFonts?.[0],
                  "}",
                  ".text-dark {",
                  tunnelData?.customFonts?.[FONT_TYPES.TITLE] || tunnelData?.conforamaFonts?.[2],
                  "}",
                  ".inline-steps > li.is-active:before {",
                  tunnelData?.conforamaBreadcrumb,
                  "}",
                  ".duration-button.selected, .duration-button:hover {",
                  tunnelData?.breadcrumbIsActiveIco,
                  "}",
                  ".btn-primary:hover, .btn-primary:not(:disabled):not(.disabled):active, .btn-primary:not(:disabled):not(.disabled).active {",
                  tunnelData?.buttonPrimaryHoverActive,
                  "}",
                  ".btn-primary:focus {",
                  tunnelData?.buttonPrimaryFocus,
                  "}",
                  ".btn-success {",
                  tunnelData?.buttonSuccess,
                  "}",
                  ".btn-success:hover, .btn-success:not(:disabled):not(.disabled):active, .btn-success:not(:disabled):not(.disabled).active {",
                  tunnelData?.buttonSuccessHoverActive,
                  "}",
                  ".btn-success:focus {",
                  tunnelData?.buttonSuccessFocus,
                  "}",
                  ".btn-outline-primary {",
                  tunnelData?.buttonOutlinePrimary,
                  "}",
                  ".service-container .warranty-table .warranty-included:before {",
                  "font-weight: 700; font-family: \"Font Awesome 5 Pro\"; position: relative; top: 2px; left: 0; content: '\\f058';",
                  "color: " + tunnelData?.checkServiceColColor + ";",
                  "font-size: 1.3rem;",
                  "}",
                  ".btn-clicked {",
                  "text-decoration: none; border: 0px solid;",
                  "background-color: " + tunnelData.chooseServiceButtonClicked + ";",
                  "}",
                  ".included-services ul li:before {",
                  "content: '\\f00c'; font-family: \"Font Awesome 5 Pro\"; font-size: 1rem;",
                  "color: " + tunnelData?.checkServiceBasketColor + ";",
                  "margin-right: 0.5rem;",
                  "}",
                  ".btn-outline-primary:hover, .btn-outline-primary:not(:disabled):not(.disabled):active, .btn-outline-primary:not(:disabled):not(.disabled).active {",
                  tunnelData?.buttonOutlinePrimaryHoverActive,
                  "}",
                  ".btn-outline-primary:focus {",
                  tunnelData?.buttonOutlinePrimaryFocus,
                  "}",
                  ".required-documents-ico {",
                  tunnelData?.requiredDocumentsIco,
                  "}",
                  ".block-steps .hard-shadow-white-block {",
                  tunnelData?.topBorderOnboarding,
                  "}",
                  ".block-steps > .block-step:before {",
                  tunnelData?.blockStepOnboarding,
                  "}",
                  ".solid-brand-block {",
                  tunnelData?.solidBrandBlock,
                  "}",
                  ".btn-tooltip .fas {",
                  tunnelData?.tooltip,
                  "}",
                  ".btn-tooltip:hover {background: none !important}",
                  ".services-included-ico {",
                  tunnelData?.servicesIncludedIco,
                  "}",
                  'input[type="radio"]:checked, input[type="checkbox"]:checked {',
                  tunnelData?.inputChecked,
                  "}"
                ].join("\n")
              )
            }}
          ></style>

          <TunnelDataContext.Provider
            value={{
              tunnelData: tunnelData,
              setTunnelData: (e) => {
                this.setState({ tunnelData: { ...tunnelData, ...e } })
              },
              onSetStep: this.handleSetStep,
              voyageurLangRevision: this.state.voyageurLangRevision
            }}
          >
            <PackServiceProvider>
              <Switch>
                <Route
                  path="/login"
                  render={(routeProps) => <LoginPage {...routeProps} {...componentProps} />}
                  key={i++}
                />
                <Route
                  path="/home"
                  render={(routeProps) => <HomePage {...routeProps} {...componentProps} />}
                  key={i++}
                />
                <Route
                  exact
                  path={["/simulation", "/simulation/:opl"]}
                  render={(routeProps) => <SimulationPage {...routeProps} {...componentProps} />}
                  key={i++}
                />
                <Route
                  path="/resume-order"
                  render={(routeProps) => <ResumeOrder {...routeProps} {...componentProps} />}
                  key={i++}
                />
                <Route
                  path="/reset-password"
                  render={(routeProps) => <ResetPasswordPage {...routeProps} {...componentProps} />}
                  key={i++}
                />
                <Route
                  path="/digital-journey"
                  render={(routeProps) => <EvollisPagesLayout {...routeProps} {...componentProps} />}
                  key={i++}
                />
              </Switch>
            </PackServiceProvider>
          </TunnelDataContext.Provider>
        </AccessKeysContext.Provider>
      )
    }
  }
}

export default PagesLayout
